import { useState } from 'react';

import {
  AssessmentMode,
  Button,
  ButtonGroup,
  FormFeedback,
  FormGroup,
  FormLabel,
} from '@opositandopn/ui';
import { IoClipboardOutline } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';

import TheoryImg from '../../../assets/images/2fe1578b-571c-4bbf-9979-db81ef945215.webp';
import { BackdropDialog } from '../../../components/shared/backdrop/BackdropDialog';
import { SectionCard } from '../../../components/shared/section-card/SectionCard';
import {
  APTITUDE_SUBJECT_ID,
  ENGLISH_SUBJECT_ID,
  THEORY_SUBJECT_ID,
} from '../../../constants/subjects';
import {
  CreateExamMultipleChoiceAssessmentAttemptRequest,
  CreateMultipleChoiceAssessmentAttemptCommandResult,
  CreateRandomMultipleChoiceAssessmentAttemptRequest,
  useCreateExamMultipleChoiceAssessmentAttempt,
  useCreateRandomMultipleChoiceAssessmentAttempt,
} from '../../../features/assessments/api/useMultipleChoiceAssessments';
import { MultipleChoiceAssessmentAttempt } from '../../../features/assessments/components/multiple-choice-assessment-attempt/MultipleChoiceAssessmentAttempt';
import { useAttemptMode } from '../../../features/assessments/useAttemptMode';
import { DifficultyLevel } from '../../../features/questions/api/useMultipleChoiceQuestions';
import { SubjectSelectableList } from '../../../features/subjects/components/subject-selectable-list/SubjectSelectableList';
import { BaseComponentProps } from '../../../types/base-component.types';

import styles from './MultipleChoiceAssessmentAttemptCard.module.scss';

type ContainerProps = BaseComponentProps & {
  subjectId: string;
  difficultyLevels?: DifficultyLevel[];
};

function MultipleChoiceAssessmentAttemptCard({
  className,
  subjectId,
  difficultyLevels,
  testId,
}: ContainerProps) {
  const navigate = useNavigate();
  const [showLessons, setShowLessons] = useState(false);

  const attemptMode = useAttemptMode(subjectId);

  const isTheorySubject = subjectId === THEORY_SUBJECT_ID;

  // Per-topic simulacro sizes; the theory subject also offers a 100-question
  // run, the rest cap out at 50.
  const questionCountOptions = isTheorySubject
    ? [15, 25, 50, 100]
    : [15, 25, 50];
  const numberOfChoices = isTheorySubject ? 3 : 4;

  const [numberOfQuestions, setNumberOfQuestions] = useState(15);
  const [examMode, setExamMode] = useState(false);

  // Reset the per-subject selections synchronously when the subject changes, so
  // the button groups render the defaults instead of the previous subject's
  // value — which may not even be on offer for the new one.
  const [selectedSubjectId, setSelectedSubjectId] = useState(subjectId);
  if (subjectId !== selectedSubjectId) {
    setSelectedSubjectId(subjectId);
    setNumberOfQuestions(15);
    setExamMode(false);
  }

  const { mutateAsync: createRandomMultipleChoiceAssessmentAttempt } =
    useCreateRandomMultipleChoiceAssessmentAttempt();
  const { mutateAsync: createExamMultipleChoiceAssessmentAttempt } =
    useCreateExamMultipleChoiceAssessmentAttempt();

  // The request the user has lined up, already paired with the endpoint that serves it.
  // Held rather than sent, because the server starts the clock when the attempt is
  // created and the mode question comes first.
  const [pendingAttempt, setPendingAttempt] = useState<{
    start: (
      mode: AssessmentMode,
    ) => Promise<CreateMultipleChoiceAssessmentAttemptCommandResult>;
  }>();

  const createAttempt = (
    request: CreateRandomMultipleChoiceAssessmentAttemptRequest,
  ) =>
    setPendingAttempt({
      start: (mode) =>
        createRandomMultipleChoiceAssessmentAttempt({ ...request, mode }),
    });

  const createExamAttempt = (
    request: CreateExamMultipleChoiceAssessmentAttemptRequest,
  ) =>
    setPendingAttempt({
      start: (mode) =>
        createExamMultipleChoiceAssessmentAttempt({ ...request, mode }),
    });

  return (
    <>
      <SectionCard
        title="Simulacro"
        image={TheoryImg}
        tooltip="Si seleccionas menos preguntas el tiempo se ajustará proporcionalmente."
        button={
          <Button
            variant="outline-primary"
            onClick={() => {
              if (examMode) {
                createExamAttempt({
                  subjectId,
                  numberOfChoices,
                  difficultyLevels,
                });
                return;
              }

              setShowLessons(true);
            }}
            suffixIcon={<IoClipboardOutline />}
          >
            {examMode ? 'Crear simulacro' : 'Seleccionar temas'}
          </Button>
        }
        className={className}
        testId={testId}
      >
        {subjectId === THEORY_SUBJECT_ID ? (
          <>
            <p>
              El temario para el examen oficial de teoría de Policía Nacional
              Escala Básica consta de 45 temas que se dividen en 3 partes:
              ciencias jurídicas, ciencias sociales y ciencias técnico
              científicas. La duración y el número de preguntas oficial es de
              100 preguntas a responder en un máximo de 50 minutos.
            </p>
            <p>
              Cada pregunta tiene tres alternativas de respuesta de las cuales
              solo una es correcta.
            </p>
          </>
        ) : subjectId === APTITUDE_SUBJECT_ID ? (
          <>
            <p>
              Simulacro para el ejercicio número uno de la prueba de aptitudes
              psicotécnicas. En este simulacro de tipo ómnibus se evaluará la
              capacidad de razonamiento, capacidad de comprensión, la aptitud
              numérica, verbal y espacial.
            </p>
            <p>
              Cada pregunta tiene cuatro alternativas de respuesta de las cuales
              solo una es correcta.
            </p>
          </>
        ) : subjectId === ENGLISH_SUBJECT_ID ? (
          <>
            <p>
              Simulacro para la prueba de inglés, los aspirantes a Escala Básica
              deberán acreditar un nivel B1 de inglés. En este test se evaluará
              la comprensión de texto, relación de ideas entre textos, tiempos
              verbales, derivación de palabras, corrección de errores
              gramaticales.
            </p>
            <p>
              Cada pregunta tiene cuatro alternativas de respuesta de las cuales
              solo una es correcta.
            </p>
          </>
        ) : null}
        <div className={styles.controls}>
          {/* The full-syllabus exam only exists for the theory subject, so the
              mode picker is hidden elsewhere. */}
          {isTheorySubject && (
            <FormGroup className={styles.control}>
              <FormLabel>Modo</FormLabel>
              <ButtonGroup
                label="Modo"
                onSelect={(value: string | number) =>
                  setExamMode(value === 'exam')
                }
                options={[
                  {
                    value: 'lessons',
                    label: 'Por temas',
                    selected: !examMode,
                  },
                  {
                    value: 'exam',
                    label: 'Modo examen',
                    selected: examMode,
                  },
                ]}
              />
              <FormFeedback>
                {examMode
                  ? 'Examen completo de 100 preguntas repartidas por todo el temario, como en el examen oficial.'
                  : 'Elige los temas que quieras practicar.'}
              </FormFeedback>
            </FormGroup>
          )}
          {/* The full-syllabus exam is fixed at 100 questions, so the size
              picker only applies when building a per-topic simulacro. */}
          {!examMode && (
            <FormGroup className={styles.control}>
              <FormLabel>Número de preguntas</FormLabel>
              <ButtonGroup
                label="Número de preguntas"
                onSelect={(value: string | number) =>
                  setNumberOfQuestions(+value)
                }
                options={questionCountOptions.map((count) => ({
                  value: count,
                  label: `${count}`,
                  selected: numberOfQuestions === count,
                }))}
              />
            </FormGroup>
          )}
        </div>
      </SectionCard>
      <SubjectSelectableList
        open={showLessons}
        subjectId={subjectId}
        onSelect={(ids) => {
          setShowLessons(false);
          createAttempt({
            subjectId,
            numberOfQuestions,
            numberOfChoices,
            difficultyLevels,
            lessonIds: ids.map((id) => id.toString()),
          });
        }}
        onClose={() => {
          setShowLessons(false);
        }}
      />
      <BackdropDialog open={pendingAttempt !== undefined}>
        <MultipleChoiceAssessmentAttempt
          mode={attemptMode}
          onStart={(mode) => pendingAttempt!.start(mode)}
          onFinish={(attempt) => {
            setPendingAttempt(undefined);
            navigate(
              `/materia/${subjectId}/simulacros/mc/resultados/${attempt.id}`,
            );
          }}
          onClose={() => setPendingAttempt(undefined)}
        />
      </BackdropDialog>
    </>
  );
}

export { MultipleChoiceAssessmentAttemptCard };
